import type {Dispatch, SetStateAction} from 'react';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import type {PreviewStreamController, UserMediaStatus} from '@pexip/media';

import type {DeviceError, DeviceErrors, InputDeviceStatusInfo} from '../types';

import {useDeviceStatusInfo} from './useDeviceStatusInfo';

export const useSubscribeToInputError = (
    setAudioInputError: Dispatch<SetStateAction<DeviceError>>,
    setVideoInputError: Dispatch<SetStateAction<DeviceError>>,
    controller?: PreviewStreamController,
) => {
    const {t} = useTranslation();
    const audioInputErrorTitle = t(
        'media.error.unable-to-use.audio',
        'Cannot use audio input device. Please select another one.',
    );
    const videoInputErrorTitle = t(
        'media.error.unable-to-use.video',
        'Cannot use video input device. Please select another one.',
    );
    useEffect(() => {
        return controller?.onAudioInputError(() => {
            setAudioInputError({
                title: audioInputErrorTitle,
            });
        });
    }, [controller, setAudioInputError, audioInputErrorTitle]);

    useEffect(() => {
        return controller?.onVideoInputError(() => {
            setVideoInputError({
                title: videoInputErrorTitle,
            });
        });
    }, [controller, setVideoInputError, videoInputErrorTitle]);
};

export const buildErrorMessages = (
    statusInfo: InputDeviceStatusInfo,
): DeviceErrors => ({
    audioError: {
        title: statusInfo.audio.error ? statusInfo.audio.title : '',
        description: statusInfo.audio.error
            ? statusInfo.audio.message
            : undefined,
        ...(statusInfo.audio.retriable ? {retriable: true} : {}),
        ...(statusInfo.audio.retryOnly ? {retryOnly: true} : {}),
    },
    videoError: {
        title: statusInfo.video.error ? statusInfo.video.title : '',
        description: statusInfo.video.error
            ? statusInfo.video.message
            : undefined,
        ...(statusInfo.video.retriable ? {retriable: true} : {}),
        ...(statusInfo.video.retryOnly ? {retryOnly: true} : {}),
    },
});

/**
 * Derives display errors directly from the current media status.
 *
 * @example
 * const {audioError} = useDeviceErrorMessage({streamStatus, requested});
 */
export const useDeviceErrorMessage = ({
    streamStatus,
    requested,
}: {
    streamStatus: UserMediaStatus;
    requested: {audio: boolean; video: boolean};
}) => {
    const deviceStatusInfo = useDeviceStatusInfo(streamStatus, {
        audio: requested.audio,
        video: requested.video,
    });
    const {audioError, videoError} = buildErrorMessages(deviceStatusInfo);
    return {
        audioError:
            deviceStatusInfo.audio.controls === 'permissions'
                ? {
                      ...audioError,
                      deniedDevice: deviceStatusInfo.audio.error,
                  }
                : audioError,
        videoError:
            deviceStatusInfo.video.controls === 'permissions'
                ? {
                      ...videoError,
                      deniedDevice: deviceStatusInfo.video.error,
                  }
                : videoError,
    };
};
